/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import useAdminDataStore, {
  selectCurrentGroupData,
} from "../../../../../store/adminData/adminData";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Alert, Divider } from "antd";

// Components
import { ContentTypeSelector } from "./components/ContentTypeSelector";
import { MatchSelector } from "./components/MatchSelector";
import { BasicFieldsSection } from "./components/BasicFieldsSection";
import { LocationSection } from "./components/LocationSection";
import { VirtualEventSection } from "./components/VirtualEventSection";
import { AnnouncementLinksSection } from "./components/AnnouncementLinksSection";
import { DetailsAndImageSection } from "./components/DetailsAndImageSection";
import { TicketLinkSection } from "./components/TicketLinkSection";
import { DisplayAndNotificationsSection } from "./components/DisplayAndNotificationsSection";

// Hooks
import { useContentFormData } from "./hooks/useContentFormData";
import { useDateTimeLogic } from "./hooks/useDateTimeLogic";
import { useFormHandlers } from "./hooks/useFormHandlers";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { useFormValidation } from "./hooks/useFormValidation";
import { useEditCopyMode } from "./hooks/useEditCopyMode";

// Utils
import {
  defaultDateConfig,
  EVENT_VIRTUAL_EVENT,
  EVENT_PODCAST,
  EVENT_NEWS,
  EVENT_UPDATE,
  EVENT_ANNOUNCEMENT,
  EVENT_WATCH_PARTY,
  EVENT_TAILGATE,
  EVENT_TRAVEL,
  EVENT_VOLUNTEERING,
  EVENT_AWAY_DAY,
  EVENT_SCREENING,
  EVENT_SPECIAL_EVENT,
  EVENT_MEET_UP,
  EVENT_TIFO,
  EVENT_FUNDRAISER,
  EVENT_SERVICE_PROJECT,
} from "./utils/constants";

// Styles
import "./styles/eventFormStyles.css";
import {
  ChantButton,
  GroupSelector,
  ScreenHeader,
} from "../../../../../CommonComponents";
import EventPoints from "../EventPoints/EventPoints";
import { useParams, useNavigate } from "react-router-dom";
import ChantContainer from "../../../../../CommonComponents/ChantContainer";
import { SkeletonForm } from "../../../../../CommonComponents/SkeletonLoaders";
import useContentFormParams from "./hooks/useContentFormParams";
import useDashboardInfo from "../../../../../hooks/useDashboardInfo";
import { toast } from "react-toastify";
import logger from "../../../../../utils/logger";

dayjs.extend(utc);

const EventForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const contentId = params.id;
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { formMode } = useContentFormParams();
  const { isParent } = useDashboardInfo();

  // Form state
  const {
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: null,
      author: "",
      location: "",
      matchId: "",
      matchName: "",
      isHome: undefined,
      awayTeam: undefined,
      title: "",
      eventStartDate: null,
      eventEndDate: null,
      eventStartTime: null,
      eventEndTime: null,
      details: "",

      notificationInUtc: [null],
      showOnHomePageStart: null,
      showOnHomePageEnd: null,
      memberResponseState: 1,
      isLinkTicket: false,
      ticketId: "",
      ticketCode: "",
      ticketName: "",
      ticketMatchId: "",
      showForFans: false,
      checkIn: { validateGeolocation: true },
      rsvpMax: null,
      volunteerRequests: [],
      videoConferenceLink: "",
      meetingCode: "",
      showOnHomePage: null,
      articleLink: "",
      podcastLink: "",
      videoLink: "",
      imageFile: "",
    },
  });

  // Watch values
  const type = watch("type");
  const title = watch("title");
  const author = watch("author");
  const matchId = watch("matchId");
  const eventStartDate = watch("eventStartDate");
  const eventStartTime = watch("eventStartTime");
  const details = watch("details");
  const notificationInUtc = watch("notificationInUtc");
  const showOnHomePage = watch("showOnHomePage");
  const showForFans = watch("showForFans");
  const ticketId = watch("ticketId");
  const ticketName = watch("ticketName");
  const ticketCode = watch("ticketCode");
  const videoConferenceLink = watch("videoConferenceLink");
  const meetingCode = watch("meetingCode");
  const articleLink = watch("articleLink");
  const podcastLink = watch("podcastLink");
  const videoLink = watch("videoLink");
  const checkIn = watch("checkIn");
  const rsvpMax = watch("rsvpMax");
  const volunteerRequests = watch("volunteerRequests");
  const imageFile = watch("imageFile");
  const eventEndDate = watch("eventEndDate");

  // Check if event is in the past
  const isPastEvent = useMemo(() => {
    if (!eventStartDate) return false;
    const now = dayjs().unix();
    return eventStartDate < now;
  }, [eventStartDate]);

  // Non-form state
  const {
    matchList,
    ticketsList,
    additionalLocations,
    setAdditionalLocations,
    isLoading,
    setIsLoading,
    editConfiguration,
    setEditConfiguration,
    oldNotificationInUtc,
    setOldNotificationInUtc,
    selectedMatchSeason,
    setSelectedMatchSeason,
    matchDate,
    setMatchDate,
    awayTeam,
    setAwayTeam,
    matchName,
    setMatchName,
    getTicketsList,
    getMatchList,
    selectedGroups,
    handleGroupSelectionChange,
    childGroupDetails,
  } = useContentFormData();

  const [setDefaults, setSetDefaults] = useState(false);

  const { handleDisplayNotificationDates } = useDateTimeLogic();
  const { validateAnnouncementLinks, validateEventData } = useFormValidation();

  const {
    handleTimeChange,
    handleDateChange,
    handleMatchChange,
    handleTicketChange,
    handleLocationChange,
    handleNotificationChange,
  } = useFormHandlers({
    setValue,
    eventStartDate,
    eventStartTime,
    matchDate,
    type,
    matchList,
    checkIn,
    ticketsList,
    setAdditionalLocations,
    notificationInUtc,
    setMatchDate,
    setSelectedMatchSeason,
    setSetDefaults,
    handleDisplayNotificationDates,
    clubId: groupData?.clubId,
    setAwayTeam,
    setMatchName,
  });

  const { handleSubmit } = useFormSubmit({
    getValues,
    setIsLoading,
    editConfiguration,
    contentId,
    oldNotificationInUtc,
    additionalLocations,
    setError,
    matchList,
    matchDate,
    isParent,
    selectedGroups,
    clubId: groupData?.clubId,
  });

  const { handleEditMode, handleCopyMode, handleViewMode } = useEditCopyMode({
    handleGroupSelectionChange,
    contentId,
    setValue,
    getValues,
    setOldNotificationInUtc,
    setAwayTeam,
    setMatchName,
    setMatchDate,
    setAdditionalLocations,
    isParent,
  });

  // Computed values
  const isAnnouncementType = [
    EVENT_PODCAST,
    EVENT_NEWS,
    EVENT_UPDATE,
    EVENT_ANNOUNCEMENT,
  ].includes(type);

  const showCheckInSection = [
    EVENT_WATCH_PARTY,
    EVENT_TAILGATE,
    EVENT_TRAVEL,
    EVENT_VOLUNTEERING,
    EVENT_AWAY_DAY,
    EVENT_SCREENING,
    EVENT_SPECIAL_EVENT,
    EVENT_MEET_UP,
    EVENT_VIRTUAL_EVENT,
    EVENT_TIFO,
    EVENT_FUNDRAISER,
    EVENT_SERVICE_PROJECT,
  ].includes(type);

  const showTicketSection = [
    EVENT_WATCH_PARTY,
    EVENT_TAILGATE,
    EVENT_TRAVEL,
    EVENT_VOLUNTEERING,
    EVENT_AWAY_DAY,
    EVENT_SCREENING,
    EVENT_SPECIAL_EVENT,
    EVENT_MEET_UP,
    EVENT_VIRTUAL_EVENT,
    EVENT_TIFO,
    EVENT_FUNDRAISER,
  ].includes(type);

  useEffect(() => {
    const initState = async () => {
      setIsLoading(true);

      try {
        const promises = [getTicketsList()];
        // Fetch match list for all modes (needed for edit to allow changing match on current events)
        promises.push(getMatchList());

        await Promise.all(promises);

        const targetGroupId = groupData?.id;

        if (formMode === "edit") {
          await handleEditMode(targetGroupId);
        } else if (formMode === "copy") {
          await handleCopyMode(targetGroupId);
        } else if (formMode === "view") {
          await handleViewMode(targetGroupId);
        }
      } catch (err) {
        toast.error("Something went wrong loading the form. Please try again.");
        logger.error("Failed to initialize event form:", err);
      } finally {
        setIsLoading(false);
      }
    };

    initState();
  }, []);

  // Handle default date configuration
  useEffect(() => {
    if (setDefaults && eventStartDate && type) {
      const configuration = defaultDateConfig[type];
      if (configuration) {
        const data = handleDisplayNotificationDates(
          eventStartDate,
          configuration,
        );
        if (data) {
          setValue("showOnHomePage", data.showOnHomePage);
          setValue("notificationInUtc", data.notificationInUtc);
        }
      }
    }
  }, [eventStartDate, type, setDefaults]);

  // Validate on submit
  const onSubmit = (e) => {
    e.preventDefault();

    // Validate parent group selection
    if (isParent && (!selectedGroups || !selectedGroups.selectionType)) {
      setError("root", {
        type: "manual",
        message: "Please select who should see this event.",
      });
      window.scrollTo(0, 0);
      return;
    }

    // Validate announcement links
    if (isAnnouncementType) {
      const linkErrors = validateAnnouncementLinks(
        articleLink,
        podcastLink,
        videoLink,
      );
      if (Object.keys(linkErrors).length > 0) {
        Object.entries(linkErrors).forEach(([field, message]) => {
          setError(field, { type: "manual", message });
        });
        setError("root", {
          type: "manual",
          message: Object.values(linkErrors).join(" "),
        });
        window.scrollTo(0, 0);
        return;
      }
    }

    // Validate event data
    const validationErrors = validateEventData({
      isAnnouncementType,
      title,
      eventStartDate,
      eventStartTime,
      eventEndDate,
      showOnHomePageEnd: watch("showOnHomePageEnd"),
      isLinkTicket: watch("isLinkTicket"),
      ticketId: watch("ticketId"),
      ticketName,
      additionalLocations,
      checkIn,
      volunteerRequests,
      rsvpMax,
    });

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
      setError("root", {
        type: "manual",
        message: Object.values(validationErrors).join(" "),
      });
      window.scrollTo(0, 0);
      return;
    }

    handleSubmit(formMode);
  };

  const handleContentTypeChange = (value) => {
    setValue("type", value);
    if (value === EVENT_VIRTUAL_EVENT) {
      setValue("checkIn", { ...checkIn, validateGeolocation: false });
    }
  };

  return (
    <ChantContainer>
      <ScreenHeader
        title={
          formMode === "edit"
            ? "Edit News/Event"
            : formMode === "copy"
              ? "Copy News/Event"
              : formMode === "view"
                ? "View News/Event"
                : "Create News/Event"
        }
      />

      <div
        style={{
          padding: "12px",
          marginTop: "var(--screen-general-margin)",
          backgroundColor: "var(--primary-white-background)",
        }}
      >
        {isLoading && <SkeletonForm fields={6} showButtons={true} />}
        {!isLoading && (
          <div className="event-form-container">
            <form>
              {errors.root && (
                <Alert
                  message="Error"
                  description={errors.root.message}
                  type="error"
                  closable
                  afterClose={() => clearErrors("root")}
                />
              )}

              <div style={{ maxWidth: "fit-content" }}>
                {isParent && (
                  <div style={{ marginBottom: "20px" }}>
                    <GroupSelector
                      selectedGroups={selectedGroups}
                      allGroups={childGroupDetails}
                      onChange={handleGroupSelectionChange}
                      typeLabel="Who should see this event?"
                    />
                    <Divider style={{ margin: "16px 0" }} />
                  </div>
                )}

                <ContentTypeSelector
                  value={type}
                  onChange={handleContentTypeChange}
                />

                <MatchSelector
                  value={matchId}
                  onChange={handleMatchChange}
                  matchList={matchList}
                  clubId={groupData?.clubId}
                  matchDate={matchDate}
                  awayTeam={awayTeam}
                  matchName={matchName}
                  isPastEvent={isPastEvent}
                />

                <BasicFieldsSection
                  title={title}
                  author={author}
                  eventStartDate={eventStartDate}
                  eventStartTime={eventStartTime}
                  onTitleChange={(e) => setValue("title", e.target.value)}
                  onAuthorChange={(e) => setValue("author", e.target.value)}
                  onDateChange={handleDateChange}
                  onTimeChange={handleTimeChange}
                  isAnnouncementType={isAnnouncementType}
                  control={control}
                />

                <LocationSection
                  additionalLocations={additionalLocations}
                  onLocationChange={handleLocationChange}
                />

                {type === EVENT_VIRTUAL_EVENT && (
                  <VirtualEventSection
                    videoConferenceLink={videoConferenceLink}
                    meetingCode={meetingCode}
                    onLinkChange={(e) =>
                      setValue("videoConferenceLink", e.target.value)
                    }
                    onCodeChange={(e) =>
                      setValue("meetingCode", e.target.value)
                    }
                  />
                )}

                {isAnnouncementType && (
                  <AnnouncementLinksSection
                    articleLink={articleLink}
                    podcastLink={podcastLink}
                    videoLink={videoLink}
                    onArticleLinkChange={(e) =>
                      setValue("articleLink", e.target.value)
                    }
                    onPodcastLinkChange={(e) =>
                      setValue("podcastLink", e.target.value)
                    }
                    onVideoLinkChange={(e) =>
                      setValue("videoLink", e.target.value)
                    }
                  />
                )}

                <DetailsAndImageSection
                  details={details}
                  imageFile={imageFile}
                  onDetailsChange={(e) => setValue("details", e.target.value)}
                  onImageSave={(image) => setValue("imageFile", image)}
                  isAnnouncementType={isAnnouncementType}
                  editConfiguration={editConfiguration}
                  setEditConfiguration={setEditConfiguration}
                />

                {showTicketSection && (
                  <TicketLinkSection
                    ticketId={ticketId}
                    ticketName={ticketName}
                    ticketCode={ticketCode}
                    ticketsList={ticketsList}
                    onChange={handleTicketChange}
                  />
                )}

                {showCheckInSection && (
                  <div style={{ marginTop: "32px" }}>
                    <EventPoints
                      offerPoints={checkIn.allowCheckin}
                      validateGeolocation={checkIn.validateGeolocation}
                      eventStartDate={eventStartDate}
                      currentSeason={checkIn.season}
                      selectedMatchSeason={selectedMatchSeason}
                      volunteerSlots={volunteerRequests}
                      checkinPoints={checkIn.points}
                      maxRsvp={rsvpMax}
                      onSeasonChange={(value) =>
                        setValue("checkIn", { ...checkIn, season: value })
                      }
                      onPointsChange={(value) =>
                        setValue("checkIn", {
                          ...checkIn,
                          points: value?.toString(),
                        })
                      }
                      onMaxRsvpChange={(value) =>
                        setValue("rsvpMax", value ?? null)
                      }
                      onVolunteerRequestsChange={(value) =>
                        setValue("volunteerRequests", value)
                      }
                      onOfferPointsChange={(value) => {
                        if (value) {
                          setValue("checkIn", {
                            ...checkIn,
                            allowCheckin: value,
                            points:
                              checkIn?.points != null ? checkIn.points : "0",
                            validateGeolocation:
                              value && type !== EVENT_VIRTUAL_EVENT
                                ? true
                                : checkIn.validateGeolocation,
                          });
                        } else {
                          setValue("checkIn", {
                            ...checkIn,
                            points: null,
                            season: null,
                            allowCheckin: false,
                            validateGeolocation: false,
                          });
                          setValue("volunteerRequests", []);
                        }
                      }}
                      onCheckLocationChange={(value) =>
                        setValue("checkIn", {
                          ...checkIn,
                          validateGeolocation: value,
                        })
                      }
                    />
                  </div>
                )}

                <DisplayAndNotificationsSection
                  showOnHomePage={showOnHomePage}
                  showForFans={showForFans}
                  notificationInUtc={notificationInUtc}
                  eventEndDate={eventEndDate}
                  onShowOnHomePageChange={(value) => {
                    setValue(
                      "showOnHomePage",
                      value ? [value[0], value[1].endOf("day")] : null,
                    );
                    if (value && value.length === 2) {
                      setValue("showOnHomePageStart", value[0]);
                      setValue("showOnHomePageEnd", value[1].endOf("day"));
                    } else {
                      setValue("showOnHomePageStart", null);
                      setValue("showOnHomePageEnd", null);
                    }
                  }}
                  onShowForFansChange={(e) =>
                    setValue("showForFans", e.target.checked)
                  }
                  onNotificationChange={handleNotificationChange}
                  onAddNotification={() =>
                    setValue("notificationInUtc", [...notificationInUtc, null])
                  }
                />
              </div>

              {formMode !== "view" && (
                <div>
                  <Divider />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "12px",
                    }}
                  >
                    {formMode === "copy" && (
                      <ChantButton
                        type="secondary"
                        disabled={isLoading}
                        onClick={() => navigate("/admin/content")}
                      >
                        Cancel
                      </ChantButton>
                    )}
                    <ChantButton
                      disabled={isLoading}
                      loading={isLoading}
                      onClick={onSubmit}
                    >
                      {isLoading ? "Submitting..." : "Submit"}
                    </ChantButton>
                  </div>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </ChantContainer>
  );
};

export default EventForm;
